<template>
  <div class="column content-box">
    <woot-button
      color-scheme="success"
      class-names="button--fixed-top mt-50 bg-primary"
      icon="arrow-download"
      @click="downloadReports"
    >
      {{ downloadButtonLabel }}
    </woot-button>
    <report-filters
      v-if="filterItemsList"
      :type="type"
      :filter-items-list="filterItemsList"
      :group-by-filter-items-list="groupByfilterItemsList"
      :selected-group-by-filter="selectedGroupByFilter"
      @date-range-change="onDateRangeChange"
      @filter-change="onFilterChange"
      @group-by-filter-change="onGroupByFilterChange"
      @business-hours-toggle="onBusinessHoursToggle"
      @inbox-filter-change="onInboxFilterChange"
    />
    <div>
      <div v-if="accountSummary.isFetching || isLoading">
        <woot-loading-state :message="$t('REPORT.LOADING_SUMMARY')" />
      </div>
      <div v-if="!accountSummary.isFetching && !isLoading" class="row">
        <woot-report-stats-card
          v-for="(metric, index) in metrics"
          :key="metric.NAME"
          :desc="metric.DESC"
          :heading="metric.NAME"
          :info-text="displayInfoText(metric.KEY)"
          :index="index"
          :on-click="changeSelection"
          :point="displayMetric(metric.KEY)"
          :trend="calculateTrend(metric.KEY)"
          :selected="index === currentSelection"
        />
      </div>

      <div class="report-bar">
        <woot-loading-state
          v-if="accountReport.isFetching || isLoading"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <div v-else class="chart-container">
          <woot-bar
            v-if="accountReport.data.length && filterItemsList.length"
            :collection="collection"
            :chart-options="chartOptions"
          />
          <span v-else class="empty-state">
            {{ $t('REPORT.NO_ENOUGH_DATA') }}
          </span>
        </div>
      </div>
      <div v-if="typeLabel">
        <label-table
          :agent-metrics="labelTable"
          :page-index="pageIndex"
          :is-loading="uiFlags.isFetchingAgentConversationMetric"
          @page-change="onPageNumberChange"
        />
        <div class="button-report-container">
          <woot-button
            v-if="typeLabel"
            color-scheme="success"
            class-names="mt-35 bg-primary"
            icon="arrow-download"
            @click="downloadLabelTable"
          >
            {{ $t('OVERVIEW_REPORTS.LABEL_TABLE.DOWNLOAD_LABEL_BUTTON') }}
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ReportFilters from './ReportFilters';
import alertMixin from 'shared/mixins/alertMixin';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import { GROUP_BY_FILTER, METRIC_CHART } from '../constants';
import LabelTable from '../LabelTable.vue';
import reportMixin from '../../../../../mixins/reportMixin';
import { formatTime } from '@chatwoot/utils';
import { generateFileName } from '../../../../../helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../../helper/AnalyticsHelper/events';
import Report from '../../../../../api/reports';

const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  INCOMING_CONVERSATIONS: 'incoming_conversations_count',
  OUTGOING_CONVERSATIONS: 'outgoing_conversations_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  HANDLING_TIME: 'avg_handling_time',
};

export default {
  components: {
    ReportFilters,
    LabelTable,
  },
  mixins: [reportMixin, alertMixin],
  props: {
    type: {
      type: String,
      default: 'account',
    },
    getterKey: {
      type: String,
      default: '',
    },
    actionKey: {
      type: String,
      default: '',
    },
    downloadButtonLabel: {
      type: String,
      default: 'Download Reports',
    },
  },
  data() {
    return {
      pageIndex: 1,
      from: 0,
      to: 0,
      currentSelection: 0,
      selectedFilter: null,
      selectedInboxIds: [],
      isLoading: true,
      groupBy: GROUP_BY_FILTER[1],
      groupByfilterItemsList: this.$t('REPORT.GROUP_BY_DAY_OPTIONS'),
      selectedGroupByFilter: null,
      businessHours: false,
    };
  },
  watch: {
    accountSummary() {
      this.isLoading = false;
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'getOverviewUIFlags',
      accountSummaryReports: 'getAccountSummary',
      labelTableSummary: 'getLabelTable'
    }),
    labelTable(){
      return this.$store.getters.getLabelTable || [];
    },
    typeLabel() {
      return this.type === 'label';
    },
    filterItemsList() {
      return this.$store.getters[this.getterKey] || [];
    },
    accountSummary() {
      return this.$store.getters.getAccountSummary || [];
    },
    accountReport() {
      return this.$store.getters.getAccountReports || [];
    },
    collection() {
      if (this.accountReport.isFetching) {
        return {};
      }
      if (!this.accountReport.data.length) return {};
      const labels = this.accountReport.data.map(element => {
        if (this.groupBy.period === GROUP_BY_FILTER[2].period) {
          let week_date = new Date(fromUnixTime(element.timestamp));
          const first_day = week_date.getDate() - week_date.getDay();
          const last_day = first_day + 6;

          const week_first_date = new Date(week_date.setDate(first_day));
          const week_last_date = new Date(week_date.setDate(last_day));

          return `${format(week_first_date, 'dd/MM/yy')} - ${format(
            week_last_date,
            'dd/MM/yy'
          )}`;
        }
        if (this.groupBy.period === GROUP_BY_FILTER[3].period) {
          return format(fromUnixTime(element.timestamp), 'MMM-yyyy');
        }
        if (this.groupBy.period === GROUP_BY_FILTER[4].period) {
          return format(fromUnixTime(element.timestamp), 'yyyy');
        }
        return format(fromUnixTime(element.timestamp), 'dd-MMM-yyyy');
      });

      const datasets = METRIC_CHART[
        this.metrics[this.currentSelection].KEY
      ].datasets.map(dataset => {
        switch (dataset.type) {
          case 'bar':
            return {
              ...dataset,
              yAxisID: 'y-left',
              label: this.metrics[this.currentSelection].NAME,
              data: this.accountReport.data.map(element => element.value),
            };
          case 'line':
            return {
              ...dataset,
              yAxisID: 'y-right',
              label: this.metrics[0].NAME,
              data: this.accountReport.data.map(element => element.count),
            };
          default:
            return dataset;
        }
      });

      return {
        labels,
        datasets,
      };
    },
    chartOptions() {
      let tooltips = {};
      if (this.isAverageMetricType(this.metrics[this.currentSelection].KEY)) {
        tooltips.callbacks = {
          label: tooltipItem => {
            return this.$t(this.metrics[this.currentSelection].TOOLTIP_TEXT, {
              metricValue: formatTime(tooltipItem.yLabel),
              conversationCount: this.accountReport.data[tooltipItem.index]
                .count,
            });
          },
        };
      }
      return {
        scales: METRIC_CHART[this.metrics[this.currentSelection].KEY].scales,
        tooltips: tooltips,
      };
    },
    metrics() {
      let reportKeys = [
        'CONVERSATIONS',
        'INCOMING_CONVERSATIONS',
        'OUTGOING_CONVERSATIONS',
      ];
      // If report type is agent, we don't need to show
      // incoming messages count, as there will not be any message
      // sent by an agent which is incoming.
      if (this.type !== 'agent') {
        reportKeys.push('INCOMING_MESSAGES');
      }
      reportKeys = [
        ...reportKeys,
        'OUTGOING_MESSAGES',
        'FIRST_RESPONSE_TIME',
        'HANDLING_TIME',
        'RESOLUTION_TIME',
        'RESOLUTION_COUNT',
      ];
      const infoText = {
        FIRST_RESPONSE_TIME: this.$t(
          `REPORT.METRICS.FIRST_RESPONSE_TIME.INFO_TEXT`
        ),
        RESOLUTION_TIME: this.$t(`REPORT.METRICS.RESOLUTION_TIME.INFO_TEXT`),
        HANDLING_TIME: this.$t(`REPORT.METRICS.HANDLING_TIME.INFO_TEXT`),
      };
      return reportKeys.map(key => ({
        NAME: this.$t(`REPORT.METRICS.${key}.NAME`),
        KEY: REPORTS_KEYS[key],
        DESC: this.$t(`REPORT.METRICS.${key}.DESC`),
        INFO_TEXT: infoText[key],
        TOOLTIP_TEXT: `REPORT.METRICS.${key}.TOOLTIP_TEXT`,
      }));
    },
  },
  mounted() {
    this.isLoading = true;
    this.$store.dispatch(this.actionKey);
  },
  methods: {
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.fetchAllData();
    },
    fetchAllData() {
      if (this.selectedFilter) {
        const { from, to, groupBy, businessHours, selectedInboxIds } = this;
        const inboxIds = selectedInboxIds.length
          ? selectedInboxIds.map(i => i.id)
          : undefined;
        this.$store.dispatch('fetchAccountSummary', {
          from,
          to,
          type: this.type,
          id: this.selectedFilter.id,
          groupBy: groupBy.period,
          businessHours,
          page: this.pageIndex,
          per_page: 10,
          inboxIds,
        });
        this.$store.dispatch('fetchLabelTable', {
          from,
          to,
          type: this.type,
          id: this.selectedFilter.id,
          groupBy: groupBy.period,
          businessHours,
          page: this.pageIndex,
          per_page: 10,
          inboxIds,
        });
        this.fetchChartData();
      }
    },
    fetchChartData() {
      const { from, to, groupBy, businessHours, selectedInboxIds } = this;
      const inboxIds = selectedInboxIds.length
        ? selectedInboxIds.map(i => i.id)
        : undefined;
      this.$store.dispatch('fetchAccountReport', {
        metric: this.metrics[this.currentSelection].KEY,
        from,
        to,
        type: this.type,
        id: this.selectedFilter.id,
        groupBy: groupBy.period,
        businessHours,
        inboxIds,
      });
    },
    downloadLabelTable(reportObj) {
      Report.getLabelTableReports(reportObj)
        .then(response => {
          downloadCsvFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'label',
            businessHours: reportObj?.businessHours,
          });
        })
        .catch(error => {
          this.showAlert(this.$t('REPORT.DOWNLOAD_FAILED'));
          console.error(error);
        });
    },
    downloadLabelTable() {
      const { from, to, type, businessHours } = this;
      const fileName = generateFileName({ type, to, businessHours });
      const params = { from, to, fileName, businessHours };
      this.downloadLabelTable(params);
    },
    downloadReports() {
      const { from, to, type, businessHours, selectedInboxIds } = this;
      const dispatchMethods = {
        agent: 'downloadAgentReports',
        label: 'downloadLabelReports',
        inbox: 'downloadInboxReports',
        team: 'downloadTeamReports',
      };
      if (dispatchMethods[type]) {
        const fileName = generateFileName({ type, to, businessHours });
        const params = { from, to, fileName, businessHours };
        if (type === 'agent' && selectedInboxIds.length) {
          params.inboxIds = selectedInboxIds.map(i => i.id);
        }
        this.$store.dispatch(dispatchMethods[type], params);
      }
    },
    changeSelection(index) {
      this.currentSelection = index;
      this.fetchChartData();
    },
    onDateRangeChange({ from, to, groupBy }) {
      // do not track filter change on inital load
      if (this.from !== 0 && this.to !== 0) {
        this.$track(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: this.type,
        });
      }

      this.from = from;
      this.to = to;
      this.groupByfilterItemsList = this.fetchFilterItems(groupBy);
      const filterItems = this.groupByfilterItemsList.filter(
        item => item.id === this.groupBy.id
      );
      if (filterItems.length > 0) {
        this.selectedGroupByFilter = filterItems[0];
      } else {
        this.selectedGroupByFilter = this.groupByfilterItemsList[0];
        this.groupBy = GROUP_BY_FILTER[this.selectedGroupByFilter.id];
      }
      this.fetchAllData();
    },
    onFilterChange(payload) {
      if (payload) {
        this.selectedFilter = payload;
        this.fetchAllData();
      }
    },
    onGroupByFilterChange(payload) {
      this.groupBy = GROUP_BY_FILTER[payload.id];
      this.fetchAllData();

      this.$track(REPORTS_EVENTS.FILTER_REPORT, {
        filterType: 'groupBy',
        filterValue: this.groupBy?.period,
        reportType: this.type,
      });
    },
    fetchFilterItems(groupBy) {
      switch (groupBy) {
        case GROUP_BY_FILTER[2].period:
          return this.$t('REPORT.GROUP_BY_WEEK_OPTIONS');
        case GROUP_BY_FILTER[3].period:
          return this.$t('REPORT.GROUP_BY_MONTH_OPTIONS');
        case GROUP_BY_FILTER[4].period:
          return this.$t('REPORT.GROUP_BY_YEAR_OPTIONS');
        default:
          return this.$t('REPORT.GROUP_BY_DAY_OPTIONS');
      }
    },
    onInboxFilterChange(inboxes) {
      this.selectedInboxIds = inboxes || [];
      this.fetchAllData();
    },
    onBusinessHoursToggle(value) {
      this.businessHours = value;
      this.fetchAllData();

      this.$track(REPORTS_EVENTS.FILTER_REPORT, {
        filterType: 'businessHours',
        filterValue: value,
        reportType: this.type,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.button-report-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  max-height: 45px;
}
</style>
